import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  formatKrw,
  formatListTime,
  jobStatusLabel,
  originWord,
  type JobSearchFilter,
} from "@makit/shared";

import { PageHead } from "../app/PageHead";
import { Button, CheckerThumb, Skeleton, SourceBadge, StatusChip } from "../components/primitives";
import { EmptyState } from "../components/feedback";
import { UploadPanel } from "../features/upload/UploadPanel";
import {
  AppliedChips,
  FindConditions,
  NO_CONDITIONS,
  conditionCount,
  makeStatusLabeller,
} from "../features/filters/FindConditions";
import { listJobs } from "../lib/api";

/** `W-20` 의 상태 칩에는 `만드는 중`이 하나 더 있다. **지금 하는 일을 훑는 자리**라서다.
 *  `W-55`(지난 것 전부)에는 없다. 키는 서버 상태값 그대로 보낸다 — 라우트가 칩 키와
 *  상태값을 함께 받는다. */
const NOW_STATUS = [{ key: "PROCESSING", label: "만드는 중" }] as const;

const statusLabel = makeStatusLabeller(NOW_STATUS);

/** 한 번에 받아 오는 줄 수. **다음 페이지가 없으면 이 값이 곧 상한이 된다** — 서버 기본값도
 *  50 이라, 61건을 가진 셀러의 오래된 11건은 어떤 조건으로도 화면에 못 나오면서 건수만
 *  61 로 적히고 있었다. */
const PAGE_SIZE = 50;

export function JobsScreen() {
  const navigate = useNavigate();
  const [conditions, setConditions] = useState<JobSearchFilter>(NO_CONDITIONS);
  const [offset, setOffset] = useState(0);
  const [dropOpen, setDropOpen] = useState<{ open: boolean; cal: boolean }>({
    open: false,
    cal: false,
  });

  /** 조건이 바뀌면 첫 페이지로 돌아간다. 안 돌리면 세 번째 페이지에서 조건을 좁혀
   *  "조건에 맞는 3건"인데 화면은 비어 있는 상태가 된다. */
  function applyConditions(next: JobSearchFilter) {
    setConditions(next);
    setOffset(0);
  }

  // 조건과 페이지까지 서버로 보낸다.
  const listQuery = useQuery({
    queryKey: ["jobs", conditions, offset],
    queryFn: () => listJobs(conditions, { limit: PAGE_SIZE, offset }),
  });

  const serverItems = listQuery.data?.items ?? [];
  const matchedCount = listQuery.data?.matchedCount ?? 0;
  const totalCount = listQuery.data?.totalCount ?? 0;
  const shownFrom = matchedCount === 0 ? 0 : offset + 1;
  const shownTo = offset + serverItems.length;
  const hasMore = shownTo < matchedCount;
  /** 한 화면에 다 들어가면 페이지 줄을 그리지 않는다. */
  const paged = matchedCount > serverItems.length;

  const hasConditions = conditionCount(conditions) > 0;
  const countLine = [
    hasConditions ? `조건에 맞는 ${matchedCount}건 · 전체 ${totalCount}건 중` : null,
    paged ? `${shownFrom}–${shownTo}번째 줄` : null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <>
      <PageHead
        frame="W-20"
        title="내 작업"
        subtitle="폰에서 온 것과 PC에서 시작한 것이 한 표에 쌓입니다"
        // ⭐ 머리 건수는 목업 `W-20` 의 `전체 18건` 이다. 조건과 무관한 이 계정의 작업 수라
        // 서버의 `totalCount` 를 그대로 쓴다. 받기 전에는 적지 않는다(`0건` 은 거짓이다).
        // ⚠️ PRD `7.7.3` 은 아직 `이번 달 18건` 으로 적혀 있다. 목업이 정본이다.
        right={listQuery.isSuccess ? `전체 ${totalCount}건` : undefined}
      />

      {/* ⛔ **「이어서 하기」 카드를 걷었다** (2026-10-04 사용자 지시). 그 카드는 처음부터 예시였다.
          「방금 폰에서 올린 것」으로 어느 작업을 고를지(넘어온 작업의 판정 · 몇 개까지인지
          `7.13` D-17)가 정본에 없어 서버 목록에서 고를 수 없었고, 그래서 남의 상품명을 단 가짜
          카드가 로그인한 셀러의 첫 화면 맨 위에 서 있었다.
          ⛔ **판정이 정해지기 전에는 그리지 않는다.** 가짜 값을 보여 주는 것보다 카드가 없는 편이
          낫다. 폰에서 올린 작업은 아래 표에 `앱` 출처로 그대로 선다. */}
      <UploadPanel onJobReady={() => void listQuery.refetch()} />

      {/* 조건 줄은 표 위다(목업 `W-20`). 예시 표가 따로 있던 동안 그 표 위에 붙어 있었다. */}
      <p className="hint" style={{ margin: "0 0 9px" }}>
        <b>기간 · 상태 · 올린 곳</b>으로 좁힐 수 있습니다. 걸린 조건은 아래 칩으로 남고, 칩의{" "}
        <span className="mono">✕</span>로 하나씩 풉니다.
      </p>

      <div
        className={`tablebar${dropOpen.open ? " dropopen" : ""}${dropOpen.cal ? " cal" : ""}`}
      >
        <AppliedChips applied={conditions} onChange={applyConditions} statusLabel={statusLabel} />
        <FindConditions
          applied={conditions}
          onApply={applyConditions}
          extraStatuses={NOW_STATUS}
          onOpenChange={(open, cal) => setDropOpen({ open, cal })}
        />
      </div>

      {listQuery.isPending ? (
        /* 불러오는 중을 "작업이 없다"로 말하지 않는다. 첫 로딩 동안 그 문구가 번쩍이면
           작업을 가진 셀러가 자기 작업이 사라진 줄 읽는다. */
        <div role="status" aria-live="polite" style={{ margin: "16px 0" }}>
          <span className="sr-only">내 작업을 불러오는 중입니다</span>
          <div style={{ display: "grid", gap: 9 }}>
            <Skeleton width="34%" height={13} />
            <Skeleton width="88%" />
            <Skeleton width="72%" />
          </div>
        </div>
      ) : listQuery.isError ? (
        /* 조회가 실패한 것과 작업이 없는 것은 다른 사실이다. 뭉개면 작업 열여덟 건을 가진
           셀러가 "아직 작업이 없습니다"를 본다. */
        <>
          <EmptyState
            art="!"
            tone="bad"
            title="작업 목록을 지금 불러올 수 없어요"
            body={
              <>
                연결이 끊겼거나 잠깐 답이 오지 않았습니다. <b>작업이 없어진 것이 아닙니다.</b>
              </>
            }
            action={<Button label="다시 시도" onClick={() => void listQuery.refetch()} />}
          />
        </>
      ) : serverItems.length === 0 ? (
        <>
          {/* W-10 — 빈 화면에도 올리는 자리를 남긴다. 웹만 열어본 셀러가 막히면 안 된다.
              조건이 걸려 있으면 "없다"가 아니라 "조건이 좁다"다. */}
          {hasConditions ? (
            /* **버튼을 세우지 않는다.** 조건을 지우는 동작은 위 조건 줄의 `모두 지우기` 칩이
               이미 갖고 있고, 조건을 고치러 가는 문은 `찾기 조건 ⌄` 이다. 여기 버튼을 세우면
               같은 동작이 한 화면에 두 번 선다. `전체 보기` 는 앱 `A-21` 의 사진 피커 열기
               하나에만 쓰는 낱말이라 조건 해제에 쓰지 않는다. 문구는 예시 표의 빈 상태가 쓰던
               것이다(조건 줄이 이제 표 위라 `위` 가 맞다). */
            <>
              <EmptyState
                art="▤"
                title="이 조건에 맞는 게 없어요"
                body={
                  <>
                    작업이 없는 게 아니라 조건이 좁습니다. 위 조건 줄의 <b>모두 지우기</b>로 한 번에
                    풀거나, <b>찾기 조건</b>에서 하나씩 고칩니다.
                  </>
                }
              />
            </>
          ) : (
            <EmptyState
              art="▤"
              title="아직 작업이 없습니다"
              body={
                <>
                  폰에서 올린 것이 여기 그대로 나타납니다.
                  <br />
                  PC에서 바로 시작하려면 위에 사진을 끌어다 놓으세요.
                </>
              }
            />
          )}
        </>
      ) : (
        <>
          <div className="tablewrap">
            <table className="jobs">
              <thead>
                <tr>
                  <th>상품</th>
                  <th>올린 곳</th>
                  <th>상태</th>
                  <th className="num">사진</th>
                  <th className="num">금액</th>
                  <th>만든 날</th>
                </tr>
              </thead>
              <tbody>
                {serverItems.map((job) => {
                  const chip = jobStatusLabel(job.status);
                  // 출처를 모르는 작업(`UNKNOWN`)은 `null` 이다. 「웹」이라 적지 않고 배지를 비운다.
                  const origin = originWord(job.origin);
                  return (
                    <tr key={job.id} onClick={() => navigate(`/jobs/${job.id}`)}>
                      <td>
                        <span className="jname">
                          <CheckerThumb size={34} />
                          <span>
                            {/* 상품명이 없으면 이름 대신 무엇을 올렸는지를 말한다. 작업
                                번호는 셀러가 보는 자리에 쓰지 않는다(PRD 7.5.9 규칙 3). */}
                            {job.productName ?? "이름 없는 작업"}
                            <span
                              className="mono"
                              style={{ display: "block", fontSize: 11, color: "var(--muted)" }}
                            >
                              {job.lastActivity?.summary ?? "아직 활동이 없습니다"}
                            </span>
                          </span>
                        </span>
                      </td>
                      <td>
                        {origin ? <SourceBadge origin={origin} /> : null}
                      </td>
                      <td>
                        <StatusChip tone={chip.tone} label={chip.label} />
                      </td>
                      <td className="num mono">
                        {job.imageDoneCount} / {job.imageTotalCount}
                      </td>
                      <td className="num mono">{formatKrw(job.billedAmount)}</td>
                      <td className="mono" style={{ fontSize: 12, color: "var(--muted)" }}>
                        {formatListTime(job.createdAt)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {/* 몇 번째 줄까지 보고 있는지를 함께 적는다. 건수만 적으면 50건 넘는 셀러가
              "61건"을 읽고도 나머지를 볼 방법을 못 찾는다. */}
          {countLine ? <p className="countline mono">{countLine}</p> : null}
          {paged ? (
            <div className="actions" style={{ marginBottom: 12 }}>
              <Button
                label="‹ 이전"
                variant="ghost"
                small
                disabled={offset === 0}
                onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
              />
              <Button
                label="다음 ›"
                variant="ghost"
                small
                disabled={!hasMore}
                onClick={() => setOffset(offset + PAGE_SIZE)}
              />
              <span className="why">한 번에 {PAGE_SIZE}건씩 보여줍니다. 최신순 고정입니다.</span>
            </div>
          ) : null}
        </>
      )}

      {/* ⛔ **설계 메모 상자를 걷었다** (2026-10-04 사용자 지시). 「올린 곳을 표시합니다…」 콜아웃,
          「이 화면에서 아직 못 합니다」, 「상품명·상태 칩·금액은 서버 값입니다」 배너는 셀러가 아니라
          설계자에게 하는 말이었다. 목업의 주석이 화면으로 옮겨진 것이다. */}
    </>
  );
}
